/**
 * Carga asincrona con estado explicito.
 *
 * Tres estados y ninguno implicito: cargando, error o dato. La UI de diagnostico
 * no puede quedarse en blanco cuando la API falla, asi que el error se conserva
 * tipado y se muestra; nunca se traga.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncResource<T> {
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
  /** Vuelve a ejecutar la carga (boton "Actualizar", o tras una accion). */
  reload: () => void;
}

interface ResourceState<T> {
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
}

/**
 * Ejecuta `load` al montar y cada vez que cambia `deps`.
 *
 * La respuesta de una carga que quedo obsoleta (la pantalla cambio de filtros
 * mientras viajaba) se descarta: no puede pisar a la carga vigente.
 */
export function useAsyncResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
): AsyncResource<T> {
  const [state, setState] = useState<ResourceState<T>>({
    data: null,
    error: null,
    loading: true,
  });
  const [nonce, setNonce] = useState(0);

  // `load` es una funcion nueva en cada render; la referencia evita relanzar la
  // carga por eso y deja que manden las dependencias declaradas.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: null }));
    loadRef.current().then(
      (data) => {
        if (!cancelled) {
          setState({ data, error: null, loading: false });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setState({ data: null, error, loading: false });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [nonce, ...deps]);

  const reload = useCallback((): void => {
    setNonce((value) => value + 1);
  }, []);

  return { data: state.data, error: state.error, loading: state.loading, reload };
}
